// ─── Copy Trade Engine (Desired-State Sync) ──────────────────
// Scans cTrader's CURRENTLY-OPEN positions in real time and writes
// them as a single "desired state" snapshot per copy pair.
//
// The MT4 EA reads this desired state and reconciles its own orders
// to MATCH it: open what's missing, close what's extra, fix SL/TP.
//
// Why this (and NOT an event queue):
//   - No more "new_position / close_position" signals stored as pending.
//   - No stale signal that fires after a position already closed.
//   - No "stop engine but signals still pending" problem.
//   - The ONLY source of truth is the live cTrader position list.
//
// Latency: SSE execution event (~100ms) → reconcile → write state (~200ms).

import { supabase } from '@/lib/supabase';
import { copyTradePositionsBus, type PositionsSnapshot } from '@/lib/copy-trade-positions-bus';
import { ensureStream, releaseStream, updateStreamSymbols } from '@/lib/background-stream-manager';
import { parseSymbolRes } from '@/lib/ctrader-parsers';
import {
  loadCopyTradeConfig,
  getCopyTradeConfigUpdatedLocally,
  type PersistedCopyPair,
} from '@/lib/copy-trade-persistence';

// ─── Types ──────────────────────────────────────────────────

interface EnginePosition {
  positionId: number;
  tradeSide: 'BUY' | 'SELL';
  symbolId?: number;
  symbolName?: string;
  volume: number;
  entryPrice: number;
  sl: number | null;
  tp: number | null;
  /** cTrader open time (Unix epoch in MILLISECONDS). Used by the MT4 EA to
   *  decide whether a not-yet-mirrored position is "late" (>5 min) and needs
   *  an explicit user approval before it is mirrored. null when unknown. */
  openTime: number | null;
}

export type VolumeMode = 'same' | 'percent' | 'fixed';

export interface CopyConfig {
  sourceAccountId: string;
  targetMt4AccountId: string;
  strategyId?: string;
  enabled?: boolean;
  volumeMode?: VolumeMode;
  volumePercent?: number;
  fixedVolume?: number;
}

// ─── Persistence keys ───────────────────────────────────────

export const COPY_TRADE_PAIRS_LS_KEY = 'copy_trade_pairs_v1';
const ENGINE_ACTIVE_LS_KEY = 'copy_trade_engine_active_v1';

// ─── Module State ───────────────────────────────────────────

let engineRunning = false;
let engineConfigs: CopyConfig[] = [];
let unsubscribeBus: (() => void) | null = null;

// Dedup: skip rewriting the desired state when a positions snapshot is
// unchanged from the last write. The shared stream now pushes a snapshot on
// every reconcile (every few seconds), so this avoids redundant DB upserts.
const lastWrittenSignature = new Map<string, string>();

// Heartbeat: even when positions are unchanged, periodically refresh the
// `updated_at` timestamp on the desired state. Without this, a static
// position list would leave `updated_at` stale and the MT4 EA's freshness
// check (default 120s) would freeze the copy — "everything looks fine but
// MT4 never opens". We refresh at most every 30s, safely under the EA
// threshold, so a live-but-unchanged account keeps the state "fresh".
const lastWrittenTime = new Map<string, number>();
const HEARTBEAT_INTERVAL_MS = 30_000;

// ─── Cross-tab / cross-device config sync ───────────────────
// The panel persists the pair config to Supabase asynchronously, and EVERY
// browser tab keeps its OWN in-memory engineConfigs. A tab opened (or left
// open) before a volume edit would otherwise keep applying the OLD transform
// forever — the classic "I set 40% but MT4 still mirrors 1:1" report — because
// nothing ever re-read the authoritative Supabase config. We therefore re-read
// it periodically (piggybacking on the incoming positions events, so it only
// runs while the engine is live) and adopt it whenever it is newer than the
// last config we applied. Supabase thus becomes the single source of truth for
// the volume transform across tabs and devices.
const CONFIG_REFRESH_THROTTLE_MS = 5_000;
let lastConfigRefreshAt = 0;
let lastSeenConfigUpdatedAt = 0;
let configRefreshInFlight = false;

// Source accounts we have already opened a shared stream for (so repeated
// config updates never leak ref-counts / restart the stream needlessly).
const streamedSourceAccounts = new Set<number>();

// Keep the last raw (untransformed) positions per source account so a manual
// "Apply" can force-rewrite desired state immediately without waiting for the
// next SSE positions event.
const lastRawPositionsByAccount = new Map<string, EnginePosition[]>();

// ─── Drop safety (generalized full-wipe guard) ──────────────
// ANY snapshot that DROPS one or more previously-mirrored positions is the
// dangerous case: the MT4 EA would close those orders even though they are
// still open on cTrader. That must never happen from a single snapshot — a
// transient/partial reconcile, a dropped packet, or a reconnect can all omit a
// position momentarily. We therefore propagate a drop only after it is
// CONFIRMED by a second consecutive snapshot, and until then we CARRY the
// dropped positions over (re-inject them) so they stay mirrored.
//
//   lastSnapshotByKey : the last desired snapshot actually written per pair
//                       (used to re-inject carried-over positions)
//   pendingDropSig    : signature of the dropped-id set awaiting confirmation
const lastSnapshotByKey = new Map<string, EnginePosition[]>();
const pendingDropSig = new Map<string, { sig: string; at: number }>();

// ─── Desired-state writer ───────────────────────────────────

/**
 * Write the CURRENT open-position list for a copy pair.
 * This is the single source of truth MT4 reconciles against.
 */
async function writeDesiredState(config: CopyConfig, positions: EnginePosition[]): Promise<void> {
  try {
    const { error } = await supabase.from('copy_trade_state').upsert(
      {
        source_account_id: config.sourceAccountId,
        target_mt4_account_id: config.targetMt4AccountId,
        strategy_id: config.strategyId || null,
        position_snapshot: positions,
        last_reconciled_at: new Date().toISOString(),
        is_active: true,
        snapshot_valid: true,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'source_account_id,target_mt4_account_id' },
    );

    if (error) {
      console.warn('[CopyTradeEngine] writeDesiredState failed:', error.message);
    } else {
      console.log(
        `[CopyTradeEngine] ✓ Desired state: ${positions.length} open position(s) — ${config.sourceAccountId} → ${config.targetMt4AccountId}`
      );
    }
  } catch (err) {
    console.warn('[CopyTradeEngine] writeDesiredState error:', err);
  }
}

// ─── Clear signature for a specific pair (used when removing a pair) ───

export function clearPairSignature(config: CopyConfig): void {
  const key = configKey(config);
  lastWrittenSignature.delete(key);
  lastWrittenTime.delete(key);
  lastSnapshotByKey.delete(key);
  pendingDropSig.delete(key);
}

// ─── Mark a pair inactive so the MT4 EA stops copying (freezes). ───

export async function deactivateDesiredState(config: CopyConfig): Promise<void> {
  try {
    const { error } = await supabase
      .from('copy_trade_state')
      .update({
        is_active: false,
        snapshot_valid: false,
        position_snapshot: [],
        updated_at: new Date().toISOString(),
      })
      .eq('source_account_id', config.sourceAccountId)
      .eq('target_mt4_account_id', config.targetMt4AccountId);

    // A row marked inactive is ignored by the MT4 EA (freeze), so clearing the
    // snapshot here can never close anything. Reset safety memory too.
    const key = configKey(config);
    lastSnapshotByKey.delete(key);
    pendingDropSig.delete(key);

    if (error) {
      console.warn('[CopyTradeEngine] deactivateDesiredState failed:', error.message);
    } else {
      console.log(`[CopyTradeEngine] ⏸ Pair deactivated + snapshot cleared: ${config.sourceAccountId} → ${config.targetMt4AccountId}`);
    }
  } catch (err) {
    console.warn('[CopyTradeEngine] deactivateDesiredState error:', err);
  }
}

// ─── Reconcile (server-driven, via SSE positions snapshot) ───
// The shared SSE stream reconciles positions ON its persistent cTrader
// WebSocket and pushes the result as a `positions` event. We no longer
// open our own cTrader WebSocket to read positions — we consume the
// snapshot that the stream already produces.

function parsePositions(snapshot: PositionsSnapshot): EnginePosition[] {
  const symbolMap = parseSymbolRes((snapshot.symbolRes as Record<string, unknown>) || {});
  const raw = snapshot.reconcileRes;
  const posArray = Array.isArray(raw.position) ? raw.position as Array<Record<string, unknown>> : [];

  return posArray.map((p) => {
    const td = (p.tradeData as Record<string, unknown>) || {};
    const volRaw = typeof td.volume === 'number' ? td.volume : 0;
    const volLots = volRaw / 10_000_000;
    const entry = typeof td.openPrice === 'number' ? td.openPrice : (typeof p.price === 'number' ? p.price : 0);
    const sideRaw = typeof td.tradeSide === 'number' ? td.tradeSide : 1;
    const side = sideRaw === 2 ? 'SELL' : 'BUY';
    const sid = typeof td.symbolId === 'number' ? td.symbolId : 0;
    const sl = typeof p.stopLoss === 'number' ? p.stopLoss : (typeof td.stopLoss === 'number' ? td.stopLoss : null);
    const tp = typeof p.takeProfit === 'number' ? p.takeProfit : (typeof td.takeProfit === 'number' ? td.takeProfit : null);

    // cTrader's ProtoOATradeData carries the position open time as
    // `openTimestamp` (Unix epoch in ms). Guard against a seconds value by
    // scaling anything that clearly is not a millisecond timestamp.
    const rawOpen = typeof td.openTimestamp === 'number'
      ? td.openTimestamp
      : (typeof p.openTimestamp === 'number' ? p.openTimestamp : 0);
    const openTime = rawOpen > 0 ? (rawOpen < 1e12 ? rawOpen * 1000 : rawOpen) : null;

    return {
      positionId: typeof p.positionId === 'number' ? p.positionId : 0,
      tradeSide: side as 'BUY' | 'SELL',
      symbolId: sid,
      symbolName: symbolMap[sid] || ('ID:' + String(sid)),
      volume: volLots,
      entryPrice: entry,
      sl,
      tp,
      openTime,
    };
  }).filter((pos) => pos.positionId > 0);
}

function configKey(config: CopyConfig): string {
  return `${config.sourceAccountId}::${config.targetMt4AccountId}`;
}

/** Round DOWN to 2 decimals (floor), guarding against float error. */
function floorTo2Decimals(value: number): number {
  return Math.floor((value + 1e-9) * 100) / 100;
}

/**
 * Apply a pair's configured volume transform to a single position volume.
 * - 'same'    → volume unchanged (1:1 with the signal).
 * - 'percent' → signal × pct%, floored to 2 decimals.
 * - 'fixed'   → a fixed lot size set by the user.
 * Any positive result below 0.01 is clamped up to 0.01 (MT4 min lot).
 */
function applyVolumeTransform(volume: number, config: CopyConfig): number {
  const mode = config.volumeMode || 'same';
  if (mode === 'same') return volume;

  let result: number;
  if (mode === 'percent') {
    const pct = typeof config.volumePercent === 'number' && config.volumePercent > 0
      ? config.volumePercent
      : 100;
    result = volume * (pct / 100);
  } else {
    // 'fixed'
    result = typeof config.fixedVolume === 'number' && config.fixedVolume > 0
      ? config.fixedVolume
      : volume;
  }

  result = floorTo2Decimals(result);
  if (result > 0 && result < 0.01) result = 0.01;
  return result;
}

function applyVolumeTransformToPositions(
  positions: EnginePosition[],
  config: CopyConfig,
): EnginePosition[] {
  if ((config.volumeMode || 'same') === 'same') return positions;
  return positions.map((p) => ({ ...p, volume: applyVolumeTransform(p.volume, config) }));
}

/** Open a shared stream once per enabled source account (idempotent). */
function ensureStreamsForConfigs(configs: CopyConfig[]): void {
  for (const config of configs) {
    if (config.enabled === false) continue;
    const accountIdNum = parseInt(config.sourceAccountId, 10);
    if (isNaN(accountIdNum) || streamedSourceAccounts.has(accountIdNum)) continue;
    streamedSourceAccounts.add(accountIdNum);
    ensureStream(accountIdNum, [], {});
    console.log(`[CopyTradeEngine] Streaming started for account ${accountIdNum}`);
  }
}

/** Stable signature of the volume-relevant parts of a config set. */
function configsSignature(configs: CopyConfig[]): string {
  return JSON.stringify(
    configs
      .map((c) => [
        String(c.sourceAccountId),
        String(c.targetMt4AccountId),
        c.enabled !== false,
        c.volumeMode || 'same',
        typeof c.volumePercent === 'number' ? c.volumePercent : null,
        typeof c.fixedVolume === 'number' ? c.fixedVolume : null,
      ])
      .sort((a, b) => {
        const ka = String(a[0]) + '::' + String(a[1]);
        const kb = String(b[0]) + '::' + String(b[1]);
        if (ka < kb) return -1;
        if (ka > kb) return 1;
        return 0;
      }),
  );
}

/**
 * Re-read the authoritative pair config from Supabase and adopt it when it is
 * newer than the last config we applied. This is what stops a stale tab (or a
 * tab opened before an edit) from keeping an old volume transform forever.
 * Best-effort and throttled: it never throws and never blocks the writer.
 */
async function maybeRefreshConfigs(): Promise<void> {
  if (!engineRunning || configRefreshInFlight) return;
  const now = Date.now();
  if (now - lastConfigRefreshAt < CONFIG_REFRESH_THROTTLE_MS) return;
  lastConfigRefreshAt = now;
  configRefreshInFlight = true;
  try {
    const cfg = await loadCopyTradeConfig();
    if (!engineRunning || !cfg || !Array.isArray(cfg.pairs)) return;
    const updatedAt = cfg.updatedAt || 0;
    // Only adopt configs STRICTLY newer than the last we applied, so a local
    // edit whose save is still in flight is never clobbered by a stale copy.
    if (updatedAt <= lastSeenConfigUpdatedAt) return;
    lastSeenConfigUpdatedAt = updatedAt;

    const remoteConfigs: CopyConfig[] = cfg.pairs
      .filter((p) => p && p.sourceAccountId && p.targetMt4AccountId)
      .map((p) => ({
        sourceAccountId: p.sourceAccountId,
        targetMt4AccountId: p.targetMt4AccountId,
        strategyId: p.strategyId,
        enabled: p.enabled !== false,
        volumeMode: p.volumeMode || 'same',
        volumePercent: p.volumePercent,
        fixedVolume: p.fixedVolume,
      }));
    if (remoteConfigs.length === 0) return;
    if (configsSignature(remoteConfigs) === configsSignature(engineConfigs)) return;

    engineConfigs = remoteConfigs;
    ensureStreamsForConfigs(engineConfigs);
    // Force a rewrite for every pair so the corrected volume transform is
    // pushed to the desired state on the very next positions event.
    for (const c of remoteConfigs) {
      lastWrittenSignature.delete(configKey(c));
      lastWrittenTime.delete(configKey(c));
    }
    console.log('[CopyTradeEngine] ⟳ Config synced from Supabase (cross-tab/device) — volume transform refreshed');
  } catch {
    /* best-effort: keep the current config on failure */
  } finally {
    configRefreshInFlight = false;
  }
}

function handlePositionsEvent(snapshot: PositionsSnapshot): void {
  if (!engineRunning) return;

  // Re-read the authoritative config from Supabase at most every few seconds so
  // a stale tab can never keep applying an old volume transform.
  void maybeRefreshConfigs();

  const accountIdStr = String(snapshot.accountId);
  const matchingConfigs = engineConfigs.filter(
    (c) => c.sourceAccountId === accountIdStr && c.enabled !== false,
  );
  if (matchingConfigs.length === 0) return;

  // Guard: only write desired state when reconcile returned a VALID position
  // array. If `position` is missing/invalid (reconcile not yet complete, or the
  // cTrader proxy omitted the repeated field), we MUST NOT write an empty
  // snapshot — that would make the MT4 EA close every mirrored order.
  const raw = snapshot.reconcileRes;
  if (!raw || !Array.isArray(raw.position)) {
    console.warn(
      `[CopyTradeEngine] ⚠️ Skipping positions event — reconcileRes.position is not a valid array (reconcile incomplete?) for account ${accountIdStr}`
    );
    return;
  }

  const rawPositions = parsePositions(snapshot);
  lastRawPositionsByAccount.set(accountIdStr, rawPositions);

  // Each config has its own volume transform, so we write a per-config
  // desired state (dedup keyed by pair, not by source account).
  for (const config of matchingConfigs) {
    let positions = applyVolumeTransformToPositions(rawPositions, config);
    const key = configKey(config);
    const now = Date.now();

    // ── Drop safety ──
    // Carry any position that is missing from this snapshot (but was present in
    // the last written one) over into the write, UNLESS the drop is confirmed
    // by two consecutive snapshots. This is what stops a transient/partial
    // reconcile from closing a live MT4 order while cTrader still holds it.
    const prevSnapshot = lastSnapshotByKey.get(key) || [];
    const newIds = positions.map((p) => p.positionId);
    const prevIds = prevSnapshot.map((p) => p.positionId);
    const droppedIds = prevIds.filter((id) => !newIds.includes(id));

    if (droppedIds.length > 0) {
      const dropSig = JSON.stringify([...droppedIds].sort((a, b) => a - b));
      const pending = pendingDropSig.get(key);
      if (pending && pending.sig === dropSig) {
        // Confirmed by a repeat → allow the drop (EA may now close those orders).
        pendingDropSig.delete(key);
        console.warn(
          `[CopyTradeEngine] ✓ Confirmed drop of ${droppedIds.length} position(s) for ${key} — propagating to MT4`
        );
      } else {
        // First sighting → carry the dropped positions over so MT4 does NOT
        // close them yet. New positions are still delivered normally.
        pendingDropSig.set(key, { sig: dropSig, at: now });
        const carry = prevSnapshot.filter((p) => droppedIds.includes(p.positionId));
        positions = [...positions, ...carry];
        console.warn(
          `[CopyTradeEngine] 🛡 Deferred drop of ${droppedIds.length} position(s) for ${key} — awaiting a confirming snapshot (prevents closing live orders)`
        );
      }
    } else {
      pendingDropSig.delete(key);
    }

    // Dedup: skip writing when the snapshot is unchanged from the last write.
    const signature = JSON.stringify(positions);
    const lastSig = lastWrittenSignature.get(key);
    const lastTime = lastWrittenTime.get(key) || 0;

    // Write when the snapshot changed OR when a heartbeat refresh is due
    // (refresh `updated_at` so the MT4 EA's freshness check never freezes a
    // live-but-unchanged account).
    if (lastSig === signature && now - lastTime < HEARTBEAT_INTERVAL_MS) {
      continue;
    }

    lastWrittenSignature.set(key, signature);
    lastWrittenTime.set(key, now);
    lastSnapshotByKey.set(key, positions);

    void writeDesiredState(config, positions);
  }

  updateStreamForPositions(snapshot.accountId, rawPositions);
}

// ─── Sync helpers ───────────────────────────────────────────

function updateStreamForPositions(accountIdNum: number, positions: EnginePosition[]): void {
  const symbolIds = positions
    .map((p) => p.symbolId)
    .filter((s): s is number => s !== undefined && s > 0);
  if (symbolIds.length > 0) {
    updateStreamSymbols(accountIdNum, symbolIds, {});
  }
}

// ─── Persistence Helpers ────────────────────────────────────

interface StoredCopyPair {
  sourceAccountId: string;
  targetMt4AccountId: string;
  strategyId?: string;
  enabled?: boolean;
  volumeMode?: VolumeMode;
  volumePercent?: number;
  fixedVolume?: number;
}

/** Read configured copy pairs from localStorage, mapped to engine configs. */
export function getPersistedCopyConfigs(): CopyConfig[] {
  try {
    const raw = localStorage.getItem(COPY_TRADE_PAIRS_LS_KEY);
    if (!raw) return [];
    const pairs = JSON.parse(raw) as StoredCopyPair[];
    return pairs
      .filter((p) => p && p.sourceAccountId && p.targetMt4AccountId)
      .map((p) => ({
        sourceAccountId: p.sourceAccountId,
        targetMt4AccountId: p.targetMt4AccountId,
        strategyId: p.strategyId,
        enabled: p.enabled !== false,
        volumeMode: p.volumeMode || 'same',
        volumePercent: p.volumePercent,
        fixedVolume: p.fixedVolume,
      }));
  } catch {
    return [];
  }
}

/** Whether the engine was running before the last page reload. */
export function wasEngineActiveBeforeReload(): boolean {
  try {
    return localStorage.getItem(ENGINE_ACTIVE_LS_KEY) === '1';
  } catch {
    return false;
  }
}

// ─── Config resolution (single source of truth) ─────────────
// The panel writes the pair config to localStorage SYNCHRONOUSLY on every edit,
// but to Supabase ASYNCHRONOUSLY. Restore used to always prefer Supabase, so if
// it read before the async save landed it started the engine with a STALE config
// (e.g. the old "40% of signal") and never re-synced — the engine kept applying
// the old volume forever. We now pick whichever copy is FRESHER (by timestamp),
// so a just-made edit always wins.

export interface ResolvedCopyTradePairs {
  pairs: PersistedCopyPair[];
  wasRunning: boolean;
}

export async function resolveCopyTradePairs(): Promise<ResolvedCopyTradePairs> {
  const supabaseConfig = await loadCopyTradeConfig();
  const supabaseUpdatedAt = supabaseConfig?.updatedAt || 0;
  const localUpdatedAt = getCopyTradeConfigUpdatedLocally();

  let localPairs: PersistedCopyPair[] = [];
  try {
    const raw = localStorage.getItem(COPY_TRADE_PAIRS_LS_KEY);
    if (raw) localPairs = JSON.parse(raw) as PersistedCopyPair[];
  } catch { /* ignore */ }

  const localHasData =
    Array.isArray(localPairs) && localPairs.some((p) => p && p.sourceAccountId && p.targetMt4AccountId);

  // localStorage wins ONLY when it holds this-browser edits newer than the
  // Supabase copy (the in-flight-save race). Otherwise Supabase stays the
  // cross-device source of truth.
  const useLocal = localHasData && localUpdatedAt > supabaseUpdatedAt;

  const supabasePairs = supabaseConfig?.pairs || [];
  return {
    pairs: useLocal ? localPairs : supabasePairs,
    wasRunning: !!supabaseConfig?.wasRunning || (useLocal && wasEngineActiveBeforeReload()),
  };
}

// ─── Public API ─────────────────────────────────────────────

/**
 * Start the copy trade engine with the given configurations.
 * Subscribes to execution events and begins periodic reconciliation.
 */
export function startCopyTradeEngine(configs: CopyConfig[]): void {
  if (engineRunning) {
    // Update configs without restarting.
    engineConfigs = [...configs];
    ensureStreamsForConfigs(engineConfigs);
    const activeCount = configs.filter((c) => c.enabled !== false).length;
    console.log(`[CopyTradeEngine] Configs updated: ${activeCount} active / ${configs.length} total`);
    return;
  }

  engineConfigs = [...configs];
  engineRunning = true;
  try { localStorage.setItem(ENGINE_ACTIVE_LS_KEY, '1'); } catch { /* ignore */ }

  // Ensure background streams are running for all enabled source accounts.
  ensureStreamsForConfigs(engineConfigs);
  const enabledConfigs = engineConfigs.filter((c) => c.enabled !== false);

  // Subscribe to positions snapshots pushed by the shared SSE stream.
  // The stream reconciles positions server-side (on connect + execution
  // events + periodically) on its single persistent WebSocket, so the engine
  // no longer opens its own cTrader WebSocket to read positions.
  unsubscribeBus = copyTradePositionsBus.subscribe((snapshot) => {
    handlePositionsEvent(snapshot);
  });

  console.log(`[CopyTradeEngine] ✓ Started with ${enabledConfigs.length} active / ${configs.length} total config(s)`);
}

/**
 * Stop the copy trade engine and clean up subscriptions.
 * Also marks all pairs inactive so the MT4 EA stops copying.
 */
export function stopCopyTradeEngine(): void {
  const configsToDeactivate = [...engineConfigs];

  engineRunning = false;
  try { localStorage.setItem(ENGINE_ACTIVE_LS_KEY, '0'); } catch { /* ignore */ }

  // Mark desired state inactive so MT4 EA freezes (no open, no close).
  for (const config of configsToDeactivate) {
    void deactivateDesiredState(config);
  }

  // Release background streams.
  for (const accountIdNum of streamedSourceAccounts) {
    releaseStream(accountIdNum);
  }
  streamedSourceAccounts.clear();

  // Forget the last-seen config so the next start adopts Supabase fresh.
  lastSeenConfigUpdatedAt = 0;

  engineConfigs = [];

  if (unsubscribeBus) {
    unsubscribeBus();
    unsubscribeBus = null;
  }

  console.log('[CopyTradeEngine] ✗ Stopped');
}

/**
 * Check if the copy trade engine is currently running.
 */
export function isCopyTradeEngineRunning(): boolean {
  return engineRunning;
}

/**
 * Force-apply the given configs immediately.
 *
 * This is the manual "Apply" fallback: it (1) updates the live engine configs
 * in place, and (2) re-writes each pair's desired state RIGHT NOW using the
 * last raw positions we've cached in memory — no need to wait for the next
 * SSE positions event. Used when the auto (on-change) sync is suspected to
 * not have taken effect.
 */
export function forceApplyConfigs(configs: CopyConfig[]): void {
  engineConfigs = [...configs];

  for (const config of configs) {
    if (config.enabled === false) continue;
    const accountIdStr = config.sourceAccountId;

    // Safety: never write an empty snapshot just because we haven't received
    // a positions event yet — that would tell MT4 to close every mirrored
    // order. Only force-apply accounts we actually have a snapshot for.
    if (!lastRawPositionsByAccount.has(accountIdStr)) {
      console.warn(
        `[CopyTradeEngine] ⚠️ Force-apply skipped for ${accountIdStr} — no positions snapshot cached yet`
      );
      continue;
    }

    const rawPositions = lastRawPositionsByAccount.get(accountIdStr) || [];
    const positions = applyVolumeTransformToPositions(rawPositions, config);

    // Clear the dedup signature so the write below is NOT skipped.
    lastWrittenSignature.delete(configKey(config));
    lastWrittenTime.delete(configKey(config));
    pendingDropSig.delete(configKey(config));
    lastSnapshotByKey.set(configKey(config), positions);

    void writeDesiredState(config, positions);
  }

  const activeCount = configs.filter((c) => c.enabled !== false).length;
  console.log(`[CopyTradeEngine] ⚡ Force-applied configs: ${activeCount} active / ${configs.length} total`);
}